<script>
import constants from "@/libs/Constants";
import ApiConnection from "@/libs/ApiConnection";
import ModalWindow from "@/components/ModalWindow.vue";
import {SimulationVariable} from "@/libs/SimulationData";
import GroupErrorTranslation from "@/libs/locales/errors";
import LineTimeSeries from "@/components/Charts/LineTimeSeries.vue";
import {
  UUIDfromList,
  downloadFileFromAElement,
  yearMonthStrToMonthI18N,
  disposeTooltipsByClass,
  setupTooltipsByClass
} from "@/libs/Helpers";
import {VariableConfiguration} from "@/libs/VisualizationData";
import OffCanvas from "@/components/OffCanvas.vue";


export default {
  name: 'PlotSimulation',
  components: {
    OffCanvas,
    ModalWindow,
    LineTimeSeries
  },
  emits: ['onCloseModalClick', 'onOpenModal', 'onTimeSeriesDataError', 'afterPlotDataLoading'],
  props: {
    propVariable: {type: VariableConfiguration, default: null},
    propSimulationData: {type: SimulationVariable, default: null},
    propPlotLayers: {type: Array, default: null},
    propPlotLayerNames: {type: Array, default: []},
    propPlotLayersCount: {type: Number, default: null},
    propPlotLayersTrigger: Boolean,
    propModalStatus: Boolean,
  },
  data() {
    return {
      constants: constants,
      plotData: null, //Data from API

      lastDataUUID: null, //UUID for data only for last month

      loading: {
        data: false, //Flag for data loading from API
        file: false,
        plot: false
      },
      offCanvasAreas: {
        show: false
      },
      modalStatus: false, //Status for modal to show
      plotDownload: false,
    }
  },
  watch: {
    propModalStatus(newValue, oldValue) {
      if (this.modalStatus !== newValue)
        this.modalStatus = newValue;
    },
    propPlotLayersTrigger(newValue, oldValue) {
      if (this.propSimulationData !== null && this.propPlotLayers !== null && this.propPlotLayers.length > 0) {
        /***
         * To avoid a request of data using the same parameters, the idea
         * is to build a UUID of the parameters (including the selected features)
         * and check whether the data is already present in the time-series or not.
         *
         * If the last UUID corresponds to the new one, then this method will only
         * send the signal to open the modal with data already loaded, otherwise
         * it makes the API request and sends the retrieved data.
         ***/
        let uuidList = [
          this.propSimulationData.simulation_id,
          this.propSimulationData.variable_id,
          this.propPlotLayers.sort().join(',')
        ];

        let newUUID = UUIDfromList(uuidList);

        if (this.plotData === null || this.lastDataUUID === null || this.lastDataUUID !== newUUID) {
          this.loading.data = true;

          ApiConnection.postSimulationTimeseries(
              this.propSimulationData.simulation_id,
              this.propSimulationData.variable_id,
              this.propPlotLayers
          ).then(response => {
            response.json().then(output => {
              //Update plot data
              this.plotData = output;

              //Update last UUID
              this.lastDataUUID = newUUID;

              //Open modal
              this.modalOpen();
            });
          }).catch((error) => {
            //Send error to parent
            this.$emit('onTimeSeriesDataError', error);
          }).finally(() => {
            this.loading.data = false;
            this.$emit('afterPlotDataLoading');
          });
        } else {
          this.$emit('afterPlotDataLoading');

          //Open modal
          this.modalOpen();
        }

        this.$nextTick(() => {
          this.tooltipsSetup();
          let object = document.getElementById('simulation-plot-offcanvas-link');
          if (object !== undefined && object !== null) {
            object.removeEventListener('click', this.offCanvasAreasShowOnClick);
            object.addEventListener('click', this.offCanvasAreasShowOnClick);
          }
        });
      }
    },
  },
  computed: {
    modalTitle() {
      let title = '';
      if (this.propVariable !== null)
        title = this.propVariable.getNameI18N(this.$i18n.locale)

      return title
    },
    plotVariableNameUnit() {
      let title = this.modalTitle;

      let unit = '';
      if (this.propVariable !== null)
        unit = this.propVariable.unit;


      if (unit !== '')
        title = `${title} [${unit}]`
      return title;
    },
    plotYAxis() {
      return this.plotVariableNameUnit;
    },
    plotTitle() {
      return this.plotVariableNameUnit;
    },
    plotFileName() {
      let name = 'simulation';
      if (this.propSimulationData !== null) {
        name = `${name}_${this.propSimulationData.variable_id}`;
      }
      return name;
    },
    plotLabels() {
      let labels = [];
      if (this.plotData !== null)
        labels = this.plotData.labels !== undefined ? this.plotData.labels : [];

      let labelsTransform = [];
      labels.forEach(value => {
        let newValue = yearMonthStrToMonthI18N(value, this.$i18n, 'long');
        labelsTransform.push(newValue);
      });
      return labelsTransform;
    },
    plotDatasets() {
      let datasets = [];

      if (this.plotData !== null && this.plotData !== undefined) {

        let areasSelectedCount = 2;
        if (this.propPlotLayers !== null && this.propPlotLayers !== undefined)
          areasSelectedCount = this.propPlotLayers.length;

        let averageData = this.plotData.features.areas;
        if (averageData !== undefined)
          datasets.push({
                type: 'line',
                label: this.$tc('InputSearchFormLabelValue.Areas', areasSelectedCount), //Series name
                backgroundColor: 'rgba(42,169,233,0.5)',
                borderColor: '#2aa9e9',
                fill: false,
                tension: 0.2,
                pointStyle: 'circle',
                pointRadius: 4,
                pointHoverRadius: 8,
                data: averageData
              }
          )

        let historicalMeanData = this.plotData.features.historic_mean;
        if (historicalMeanData !== undefined)
          datasets.push({
            type: 'line',
            label: this.$t('InputSearchFormLabelValue.HistoricMean'), //Series name
            backgroundColor: 'rgba(126, 26, 125, 0.5)',
            borderColor: 'rgb(126, 26, 125)',
            pointBorderWidth: 1,
            borderWidth: 0, // No line
            fill: false,
            tension: 0.2,
            pointStyle: 'rectRot',
            pointRadius: 6,
            pointHoverRadius: 8,
            data: historicalMeanData
          });
      }
      return datasets;
    },
  },
  methods: {
    descriptionLabelI18NGet() {
      let params = {variable: this.modalTitle};

      let labelPostfix = 'area_one';
      let labelTooltipContent = '&nbsp;';

      if (this.propPlotLayers !== null && this.propPlotLayersCount !== null) {
        let selectedLayerCount = this.propPlotLayers.length;

        if (selectedLayerCount > 0 && this.propPlotLayersCount > 0) {
          if (selectedLayerCount === 1) {
            labelPostfix = 'area_one';

            if (this.propPlotLayerNames !== null) {
              labelTooltipContent = this.propPlotLayerNames.toString();
              labelTooltipContent = labelTooltipContent.replaceAll('"', "&#34;");
            }
          } else if (selectedLayerCount < this.propPlotLayersCount) {
            labelPostfix = 'area_many'
            params.area_count = selectedLayerCount;
          } else
            labelPostfix = 'area_all'
        }
      }

      params.tooltip_content = labelTooltipContent;
      return this.$t(`VisualizationPlot.simulation.${labelPostfix}`, params);
    },
    modalOpen() {
      this.modalStatus = true;
      this.$emit('onOpenModal');
    },
    modalOnClose() {
      this.modalStatus = false;
      this.$emit('onCloseModalClick');
    },
    downloadImageButtonOnClick() {
      this.plotDownload = !this.plotDownload;
    },
    downloadFileButtonOnClick() {
      this.loading.data = true;
      this.loading.file = true;

      ApiConnection.postSimulationTimeseriesDownload(
          this.propSimulationData.simulation_id,
          this.propSimulationData.variable_id,
          this.propPlotLayers
      ).then(response => {
        let fileName = response.headers.get('filename');
        if (fileName === null)
          fileName = `${this.plotFileName}.csv`

        response.blob().then(data => {
          /*Create link with BLOB data to download*/
          downloadFileFromAElement(fileName, data);
        });
      }).catch((error) => {
        //Send error to parent
        this.$emit('onTimeSeriesDataError', GroupErrorTranslation.t(error.message, this.$i18n));
      }).finally(() => {
        this.loading.data = false;
        this.loading.file = false;
      });
    },
    tooltipsDispose() {
      disposeTooltipsByClass('.tooltip-modal-init');
    },
    tooltipsSetup() {
      this.tooltipsDispose();
      setupTooltipsByClass('.tooltip-modal-init');
    },
    offCanvasAreasShowOnClick() {
      this.offCanvasAreas.show = true;
    },
    offCanvasAreasOnHidden() {
      this.offCanvasAreas.show = false;
    }
  }
}
</script>

<template>
  <ModalWindow prop-id="modalGraphSimulation"
               prop-size="lg"
               :prop-static="true"
               :prop-status="modalStatus"
               @on-close-modal-click="modalOnClose">
    <template v-slot:title>
        <span class="h5 text-primary pe-1 fw-semibold">
          {{ $t('SimulationInputs.Simulation') }}
        </span>{{ modalTitle }}
    </template>
    <div class="row">
      <div class="col mb-1">
        <p class="fw-light lh-sm">
          <span v-html="descriptionLabelI18NGet()"></span>
          <span v-html="$t('VisualizationPlot.simulation.download')"></span>
        </p>
      </div>
    </div>
    <LineTimeSeries
        :prop-title="plotTitle !== undefined && plotTitle !== null ? plotTitle : ''"
        :prop-labels="this.plotLabels"
        :prop-datasets="this.plotDatasets"
        :prop-download-toggle="plotDownload"
        :prop-download-file-name="plotFileName"
        :prop-y-axis-label="this.plotYAxis"/>
    <template v-slot:footer>
      <div class="btn-group dropup">
        <button type="button" class="btn btn-success dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false"
                :disabled="loading.data === true">
          <div v-if="loading.file === true" class="spinner-grow spinner-grow-sm me-2" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <i class="bi bi-download pe-2" v-else></i>
          <span class="pe-1">{{ $t('buttons.downloadData') }}</span>
        </button>
        <ul class="dropdown-menu">
          <li>
            <button class="dropdown-item" role="link" @click="downloadImageButtonOnClick"
                    :disabled="loading.data === true">
              <i class="bi bi-image pe-2"></i>{{ $t('buttons.downloadDataImage') }}
            </button>
          </li>
          <li>
            <hr class="dropdown-divider">
          </li>
          <li>
            <button class="dropdown-item" role="link" @click="downloadFileButtonOnClick"
                    :disabled="loading.data === true">
              <i class="bi bi-file-earmark-spreadsheet pe-2"></i>{{ $t('buttons.downloadDataCSV') }}
            </button>
          </li>
        </ul>
      </div>
    </template>
    <OffCanvas
        prop-id="plotSimulationOffCanvas"
        :prop-title="$tc('InputSearchFormLabelValue.Areas', propPlotLayerNames.length)"
        :prop-show="offCanvasAreas.show"
        prop-position="end"
        :prop-hide-close-button="false"
        @on-canvas-hidden="offCanvasAreasOnHidden">
      <table class="table table-sm table-hover table-bordered table-striped">
        <tbody>
        <tr v-for="(area, index) in propPlotLayerNames">
          <td class="fw-semibold text-end">{{ index + 1 }}</td>
          <td>{{ area }}</td>
        </tr>
        </tbody>
      </table>
    </OffCanvas>
  </ModalWindow>
</template>